import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LinkStyle} from '@ui/buttons/external-link';
import {RegisterPayload, useRegister} from '../requests/use-register';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '@ui/settings/use-settings';
import React, {useContext, useEffect, useState} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {setInLocalStorage} from '@ui/utils/hooks/local-storage';

interface Props {
  inviteType?: string;
}
export function RegisterPage({inviteType}: Props) {
  const {branding, registration, social} = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const isRegisteringUsingInvite =
    pathname.includes('workspace') || !!inviteType;
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const isDriveShareRegister = searchParams.get('redirectFrom') === 'drive/share';
  const searchParamsEmail = searchParams.get('email') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: {email: searchParamsEmail},
  });
  const register = useRegister(form);

  // Store drive/share redirect or URL plan selection in localStorage for redirect after registration
  useEffect(() => {
    if (isDriveShareRegister) {
      setInLocalStorage('be.redirectToDriveShare', 'true');
    }

    const pid = searchParams.get('productId') || searchParams.get('product_id') || searchParams.get('plan_id');
    const prId = searchParams.get('priceId') || searchParams.get('price_id');
    if (pid && prId) {
      setInLocalStorage('be.onboarding.selected', {
        productId: parseInt(pid, 10) || pid,
        priceId: parseInt(prId, 10) || prId,
      });
    }
  }, [isDriveShareRegister, searchParams]);

  if (registration?.disable && !isRegisteringUsingInvite) {
    return <Navigate to="/login" replace />;
  }

  let heading = <Trans message="Create a new account" />;
  if (isRegisteringUsingInvite) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  } else if (isDriveShareRegister) {
    heading = <Trans message="Create an account to view shared files" />;
  }

  const message = !registration?.disable && (
    <div className="flex flex-col items-center gap-12 mt-16">
      <div>
        <Trans
          values={{
            a: parts => (
              <Link className={LinkStyle} to="/login">
                {parts}
              </Link>
            ),
          }}
          message="Already have an account? <a>Sign in.</a>"
        />
      </div>
      <div>
        <span>Got a query? </span>
        <Link to="/contact" className={LinkStyle}>
          Contact us.
        </Link>
      </div>
    </div>
  );

  useEffect(() => {
    if (register.error) {
      setIsLoading(false);
    }
  }, [register.error]); // Runs only when register.error changes

  return (
    <AuthLayout heading={heading} message={message}>
      {isLoading ? (
        <FullPageLoader />
      ) : (
        <>
          <StaticPageTitle>
            <Trans message="Register" />
          </StaticPageTitle>
          <Form
            form={form}
            onSubmit={async payload => {
              const isValid = await verify();
              if (isValid) {
                console.log(register);

                setIsLoading(true);
                register.mutate({
                  ...payload,
                  invite_type: inviteType,
                });
              }
            }}
          >
            <FormTextField
              className="mb-32"
              name="email"
              type="email"
              disabled={!!searchParamsEmail}
              label={<Trans message="Email" />}
              required
            />

            <FormTextField
              className="mb-32"
              name="password"
              type={showNew ? 'text' : 'password'}
              label={<Trans message="Password" />}
              required
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="ml-8 pr-4 text-gray-600 hover:text-primary focus:outline-none"
                  tabIndex={-1}
                >
                  {showNew ? (
                    // Eye Slash SVG
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 3l18 18" />
                  </svg>
                  ) : (
                    // Eye Open SVG (larger)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                  </svg>
                  )}
                </button>
              }
            />

            <FormTextField
              className="mb-32"
              name="password_confirmation"
              type={showConfirm ? 'text' : 'password'}
              label={<Trans message="Confirm Password" />}
              required
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="ml-8 pr-4 text-gray-600 hover:text-primary focus:outline-none"
                  tabIndex={-1}
                >
                  {showConfirm ? (
                    // Eye Slash SVG
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 3l18 18" />
                  </svg>
                  ) : (
                    // Eye Open SVG (larger)
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                            4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                  </svg>
                  )}
                </button>
              }
            />
            {auth?.registerFields ? <auth.registerFields /> : null}
            <PolicyCheckboxes />
            <Button
              className="block w-full"
              type="submit"
              variant="flat"
              color="primary"
              size="md"
              disabled={register.isPending || isVerifying}
          //     style={{
          //   backgroundColor: '#6cf3fd',
          //   color: '#000',
          // }}
            >
              <Trans message="Create an account" />
            </Button>
            <SocialAuthSection
              dividerMessage={
                social?.compact_buttons ? (
                  <Trans message="Or sign up with" />
                ) : (
                  <Trans message="OR" />
                )
              }
            />
          </Form>
        </>
      )}
    </AuthLayout>
  );
}

function PolicyCheckboxes() {
  const {registration} = useSettings();

  if (!registration?.policies) return null;

  return (
    <div className="mb-32">
      {registration.policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="mb-4 block"
          required
        >
          <Trans
            message="I accept the :name"
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}